import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Camera, Upload, X } from 'lucide-react';
import { toast } from '@/shared/hooks/use-toast';
import {
  useAddPhotoWithHotspotGrouping,
  useUploadImageToHotspot,
  useHotspotOverlayGroups,
  useCreateHotspotOverlayGroup,
  useUpdateOverlay,
} from '../../hooks/hooks';
import { ViewPosition } from '../../types/types';
import { ENABLE_PHOTO_GROUPS } from '../../constants/constants';

interface PhotoFormProps {
  photoId: number;
  viewPosition: ViewPosition;
  onSuccess?: () => void;
  onCancel?: () => void;
  hotspotId?: number;
}

const PhotoForm: React.FC<PhotoFormProps> = ({
  photoId,
  viewPosition,
  onSuccess,
  onCancel,
  hotspotId,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [title, setTitle] = useState<string>('');
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [groupMode, setGroupMode] = useState<'existing' | 'new'>('new');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [newGroupName, setNewGroupName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const { addPhotoWithHotspotGrouping, isLoading: isCreatingNewHotspot } =
    useAddPhotoWithHotspotGrouping();
  const { mutateAsync: uploadImageToHotspot, isPending: isUploadingToHotspot } =
    useUploadImageToHotspot();
  const { data: groupsData } = useHotspotOverlayGroups(ENABLE_PHOTO_GROUPS ? hotspotId : undefined);
  const { mutateAsync: createOverlayGroup, isPending: isCreatingGroup } =
    useCreateHotspotOverlayGroup();
  const { mutateAsync: updateOverlay, isPending: isUpdatingOverlay } = useUpdateOverlay();

  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();

      const isMobile = /iphone|ipod|android.*mobile|windows phone|blackberry|nokia/i.test(
        userAgent
      );

      const isTablet =
        /ipad|android(?!.*mobile)|tablet|kindle|silk|playbook|bb10|surface|touch/i.test(
          userAgent
        ) ||
        (navigator.maxTouchPoints > 1 && /windows/i.test(userAgent)) ||
        (/windows/i.test(userAgent) && 'ontouchstart' in window) ||
        (navigator.maxTouchPoints > 1 && !isMobile);

      const isWindowsTouch = 'ontouchstart' in window && /windows/i.test(userAgent);

      const hasCameraSupport =
        'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;

      const shouldShowCamera =
        isMobile ||
        isTablet ||
        isWindowsTouch ||
        (navigator.maxTouchPoints > 0 && hasCameraSupport);

      setIsMobileOrTablet(shouldShowCamera);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  useEffect(() => {
    const urls = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [selectedFiles]);

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > 0) {
      setSelectedFiles((currentFiles) => [...currentFiles, ...files]);
    }
    event.target.value = '';
  };

  const handleCameraInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFiles((currentFiles) => [...currentFiles, file]);
    }
    event.target.value = '';
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Enhanced camera capture with fallback
  const handleCameraCaptureWithFallback = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    } else {
      // Fallback to regular file input if camera input fails
      console.warn('Camera input not available, falling back to file upload');
      toast({
        title: 'Camera not available',
        description: 'Using file upload instead. You can select photos from your camera roll.',
        variant: 'default',
        className: 'bg-blue-500 text-white',
      });
      handleUploadClick();
    }
  };

  const handleRemovePhoto = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    setTitle('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      toast({
        title: 'No photos selected',
        description: 'Please select or take at least one photo to continue.',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
      });
      return;
    }

    let uploadedCount = 0;
    let failedCount = 0;
    let targetHotspotId = hotspotId;
    let targetGroupId: number | undefined;
    if (ENABLE_PHOTO_GROUPS && hotspotId) {
      if (groupMode === 'existing') {
        if (!selectedGroupId) {
          toast({
            title: 'Group required',
            description: 'Select an existing group or switch to create a new one.',
            variant: 'destructive',
            className: 'bg-red-500 text-white',
          });
          return;
        }
        targetGroupId = Number(selectedGroupId);
      } else {
        try {
          const createdGroup = await createOverlayGroup({
            hotspotId,
            name: newGroupName.trim() || undefined,
          });
          targetGroupId = createdGroup.id;
        } catch (error) {
          console.error('Failed to create group:', error);
          toast({
            title: 'Group creation failed',
            description: 'Unable to create a new group. Please try again.',
            variant: 'destructive',
            className: 'bg-red-500 text-white',
          });
          return;
        }
      }
    }

    for (const file of selectedFiles) {
      try {
        if (targetHotspotId) {
          await uploadImageToHotspot({
            hotspotId: targetHotspotId,
            file,
            title: title || undefined,
            groupId: targetGroupId,
          });
        } else {
          const result = await addPhotoWithHotspotGrouping({
            photoId,
            position: viewPosition,
            file,
            title: title || undefined,
            groupId: targetGroupId,
          });
          // Ensure all photos from this submission are attached to one hotspot.
          targetHotspotId = result.hotspotId;

          // For new hotspots, create one group for this batch and move the first uploaded photo into it.
          if (ENABLE_PHOTO_GROUPS && !targetGroupId) {
            try {
              const createdGroup = await createOverlayGroup({
                hotspotId: targetHotspotId,
              });
              targetGroupId = createdGroup.id;

              const firstOverlayId = Number((result.overlay as { id?: number })?.id);
              if (Number.isFinite(firstOverlayId)) {
                await updateOverlay({
                  hotspotId: targetHotspotId,
                  overlayId: firstOverlayId,
                  data: { group_id: targetGroupId },
                });
              } else {
                throw new Error('Missing first overlay id for initial grouping');
              }
            } catch (groupingError) {
              console.error('Failed to create/apply batch group:', groupingError);
              throw groupingError;
            }
          }
        }
        uploadedCount += 1;
      } catch (error) {
        failedCount += 1;
        console.error(`Error adding photo ${file.name}:`, error);

        // For new-hotspot grouped flow, do not continue if the initial group handoff failed.
        if (ENABLE_PHOTO_GROUPS && !hotspotId && targetHotspotId && !targetGroupId) {
          toast({
            title: 'Group setup failed',
            description:
              'Could not create/assign a group for this new hotspot batch. Please try again.',
            variant: 'destructive',
            className: 'bg-red-500 text-white',
          });
          return;
        }
      }
    }

    if (uploadedCount > 0) {
      toast({
        title: 'Success',
        description:
          failedCount > 0
            ? `${uploadedCount} photo(s) added. ${failedCount} failed to upload.`
            : `${uploadedCount} photo(s) added successfully!`,
        variant: 'default',
        className: 'bg-green-500 text-white',
      });

      onSuccess?.();
      return;
    }

    if (failedCount > 0) {
      toast({
        title: 'Error',
        description: 'Failed to add photos. Please try again.',
        variant: 'destructive',
        className: 'bg-red-500 text-white',
      });
    }
  };

  const isLoading = isCreatingNewHotspot || isUploadingToHotspot || isCreatingGroup || isUpdatingOverlay;

  return (
    <div className="space-y-4">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraInputChange}
        className="hidden"
      />

      {selectedFiles.length === 0 ? (
        <div className="space-y-4">
          {ENABLE_PHOTO_GROUPS && hotspotId && (
            <div className="space-y-3 rounded-md border p-3">
              <Label className="text-sm">Upload Destination</Label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="upload-destination"
                    checked={groupMode === 'existing'}
                    onChange={() => setGroupMode('existing')}
                    disabled={isLoading}
                  />
                  <span className="text-sm">Add to existing group</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    name="upload-destination"
                    checked={groupMode === 'new'}
                    onChange={() => setGroupMode('new')}
                    disabled={isLoading}
                  />
                  <span className="text-sm">Create a new group</span>
                </label>
              </div>

              {groupMode === 'existing' ? (
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="">Select a group</option>
                  {(groupsData?.groups || []).map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name?.trim() || `Group ${group.id}`} ({group.overlay_count})
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Optional group name"
                  disabled={isLoading}
                />
              )}
            </div>
          )}

          {/* Title input field - available upfront */}
          <div className="space-y-2">
            <Label htmlFor="image-title-initial">Photo Title (Optional)</Label>
            <Input
              id="image-title-initial"
              type="text"
              placeholder="Enter a title for uploaded photos..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {isMobileOrTablet && (
            <Card
              className="p-6 cursor-pointer hover:border-primary transition-colors"
              onClick={handleCameraCaptureWithFallback}
            >
              <div className="flex flex-col items-center space-y-2">
                <Camera className="w-12 h-12 text-primary" />
                <h3 className="font-semibold">Take Photo</h3>
                <p className="text-sm text-muted-foreground text-center">
                  Take a photo using your camera (available on phones and tablets)
                </p>
              </div>
            </Card>
          )}

          <Card
            className="p-6 cursor-pointer hover:border-primary transition-colors"
            onClick={handleUploadClick}
          >
            <div className="flex flex-col items-center space-y-2">
              <Upload className="w-12 h-12 text-primary" />
              <h3 className="font-semibold">Upload Photos</h3>
              <p className="text-sm text-muted-foreground text-center">
                Choose one or more photos from your gallery or files
              </p>
            </div>
          </Card>
        </div>
      ) : (
        <div className="space-y-4">
          <Card className="p-3">
            <div className="grid grid-cols-3 gap-2">
              {previewUrls.slice(0, 6).map((url, index) => (
                <img
                  key={`${selectedFiles[index].name}-${selectedFiles[index].lastModified}-${index}`}
                  src={url}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-24 object-cover rounded-md"
                />
              ))}
            </div>
            {selectedFiles.length > 6 && (
              <p className="text-xs text-muted-foreground mt-2">
                +{selectedFiles.length - 6} more photo(s)
              </p>
            )}
          </Card>

          <div className="text-sm text-muted-foreground">
            <p>{selectedFiles.length} photo(s) selected</p>
            <p>
              Total size:{' '}
              {(selectedFiles.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>

          {/* Title input field */}
          <div className="space-y-2">
            <Label htmlFor="image-title">Title (Optional)</Label>
            <Input
              id="image-title"
              type="text"
              placeholder="Enter a title for all selected photos..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <Button
            type="button"
            variant="secondary"
            onClick={handleUploadClick}
            disabled={isLoading}
            className="w-full"
          >
            <Upload className="h-4 w-4 mr-2" />
            Add More Photos
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleRemovePhoto}
            disabled={isLoading}
            className="w-full"
          >
            <X className="h-4 w-4 mr-2" />
            Clear Selected Photos
          </Button>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        {selectedFiles.length > 0 && (
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? `Adding ${selectedFiles.length} photo(s)...`
              : `Add ${selectedFiles.length} Photo${selectedFiles.length > 1 ? 's' : ''}`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PhotoForm;
